import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ApsPermitedScreen } from '../../../models/aps-permission/aps-permited-screen';
import { ConstantsFordProtect } from '../../../models/constants/constants-ford-protect';
import { Patterns } from '../../../models/constants/patterns';
import { NewPrice } from '../../../models/product/new-price';
import { ApsPermissionService } from '../../../services/aps-permission/aps-permission.service';
import { PriceService } from '../../../services/price/price.service';

type ValueChangeEvent<T> = { value: T };

@Component({
  selector: 'app-new-price',
  templateUrl: './new-price.component.html',
  styleUrls: ['./new-price.component.scss'],
  standalone: false
})
export class NewPriceComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly focusOrder: string[] = [
    'optionNamePrice',
    'optionCurrency',
    'inputPriceValue',
    'inputPriceDescription'
  ];

  listNamePrice = [
    { label: 'TRANSLATE.SELECT' },
    { label: 'TRANSLATE.SELECT.PRICE.CUSTOMER', value: ConstantsFordProtect.PRICE_CUSTOMER },
    { label: 'TRANSLATE.SELECT.SELLER.PRICE', value: ConstantsFordProtect.SELLER_PRICE },
    { label: 'TRANSLATE.SELECT.COST.FORD', value: ConstantsFordProtect.COST_FORD },
    { label: 'TRANSLATE.SELECT.COST.WARRANTY', value: ConstantsFordProtect.COST_WARRANTY },
    { label: 'TRANSLATE.SELECT.COST.SERVICE', value: ConstantsFordProtect.COST_SERVICE }
  ];

  listCurrency = [
    { label: 'TRANSLATE.SELECT' },
    { label: 'TRANSLATE.SELECT.COUNTRY.CURRENCY.BRA', value: ConstantsFordProtect.REAL },
    { label: 'TRANSLATE.SELECT.COUNTRY.CURRENCY.OTHERS', value: ConstantsFordProtect.PESO }
  ];

  userform!: UntypedFormGroup;
  selectedNamePrice: string | null = '';
  selectedCurrency: string | null = '';
  inputPrice: string = '';
  finalPrice: number = 0;
  isSaving = false;
  submitted = false;
  isCtaSuccess = false;
  private debounceTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private fb: UntypedFormBuilder,
    private messageService: MessageService,
    private apsPermissionService: ApsPermissionService,
    private translateService: TranslateService,
    private priceService: PriceService,
    private router: Router
  ) { }

  ngOnInit() {

    this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenViewsPlans);

    this.userform = this.fb.group({
      'optionNamePrice': [null, Validators.required],
      'optionCurrency': [null, Validators.required],
      'inputPriceValue': ['', [Validators.required, Validators.min(1), Validators.maxLength(13), Validators.pattern(Patterns.COMMA_AND_SEMICOLON_NUMBERS)]],
      'inputPriceDescription': ['', [Validators.required, Validators.minLength(4), Validators.maxLength(100)]]
    });

    // Smart debounce with status tracking
    // Listen to form validity changes separately from value changes
    this.userform.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => {
        // Immediately disable button if form becomes invalid
        if (status !== 'VALID') {
          clearTimeout(this.debounceTimer);
          this.isCtaSuccess = false;
        }
      });

    // Listen to value changes and debounce the enable
    this.userform.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // If form is not valid, skip debounce
        if (this.userform?.status !== 'VALID') {
          return;
        }

        // Form is valid - clear old timer and start new debounce
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          // Only enable if form is still valid after debounce
          if (this.userform?.status === 'VALID') {
            this.isCtaSuccess = true;
          }
        }, 1500);
      });
  }

  private checkFormCompletion(): void {
    this.isCtaSuccess = this.userform?.valid ?? false;
  }

  formatNumber(event: Event) {

    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }
    let priceTreatment = input.value;

    priceTreatment = priceTreatment.replace(/\D/g, "");
    priceTreatment = priceTreatment.replace(/(\d+)(\d{2})$/, "$1,$2");
    priceTreatment = priceTreatment.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");

    input.value = priceTreatment;

    const valueWithoutPoint = priceTreatment.replace(/\./g, '').replace(',', '.');
    this.inputPrice = valueWithoutPoint;
  }

  private parseInputPriceValue(): number | undefined {
    if (!this.userform) {
      return undefined;
    }

    const rawValue = (this.userform.get('inputPriceValue')?.value || '').toString().trim();
    if (!rawValue) {
      return undefined;
    }

    const normalizedValue = rawValue.replace(/\./g, '').replace(',', '.');
    const parsedValue = Number(normalizedValue);
    return Number.isNaN(parsedValue) ? undefined : parsedValue;
  }

  buttonReturn() {
    this.router.navigate(['view-plans'], { skipLocationChange: false, replaceUrl: false });
  }

  namePriceChange(event: ValueChangeEvent<string | null>) {
    this.selectedNamePrice = event.value;
  }

  currencyChange(event: ValueChangeEvent<string | null>) {
    this.selectedCurrency = event.value;
  }


  buttonSave() {
    if (this.isSaving) {
      return;
    }

    this.submitted = true;

    if (!this.userform) {
      return;
    }

    const formValues = this.userform.value;
    this.selectedNamePrice = formValues.optionNamePrice;
    this.selectedCurrency = formValues.optionCurrency;

    if (this.selectedNamePrice === undefined || this.selectedNamePrice === null) {
      this.userform.get('optionNamePrice')?.markAsTouched();
      this.selectedNamePrice = null;
    }
    if (this.selectedCurrency === undefined || this.selectedCurrency === null) {
      this.userform.get('optionCurrency')?.markAsTouched();
      this.selectedCurrency = null;
    }
    if (this.userform.get('inputPriceValue')?.invalid) {
      this.userform.get('inputPriceValue')?.markAsPending();
    }
    if (this.userform.get('inputPriceDescription')?.invalid) {
      this.userform.get('inputPriceDescription')?.markAsPending();
    }
    if (this.userform.valid) {

      const itemPrice = new NewPrice();
      const normalizedPrice = this.inputPrice ??
        (formValues.inputPriceValue || '').toString().replace(/\./g, '').replace(',', '.');
      this.finalPrice = parseFloat(normalizedPrice);

      if (!Number.isFinite(this.finalPrice) || this.finalPrice <= 0) {
        this.userform.get('inputPriceValue')?.markAsPending();
        this.focusField('inputPriceValue');
        return;
      }

      itemPrice.itemName = this.selectedNamePrice || '';
      itemPrice.itemPriceUnit = this.selectedCurrency || '';
      itemPrice.itemPrice = this.finalPrice;
      itemPrice.itemDescription = (formValues.inputPriceDescription || '').toString().trim();
      itemPrice.createUser = this.apsPermissionService.getUserCsid() || sessionStorage.getItem('userId') || '';

      this.isSaving = true;
      this.priceService.createNewPrice(itemPrice)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (_response) => {

            this.isSaving = false;
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('TRANSLATE.SUCCESS'),
              detail: this.translateService.instant('TRANSLATE.SUCCESS.CREATE')
            });

            this.buttonReturn();
          },
          error: (err) => {

            if (err.status === 403) {
              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('TRANSLATE.ERROR'),
                detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
              });
              return;
            }

            this.isSaving = false;
            if (err.status === 402) {
              this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Pagamento recusado. Verifique os dados do cartão e tente novamente.' })
              return;
            }
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('TRANSLATE.ERROR'),
              detail: this.translateService.instant('TRANSLATE.ERROR.CREATE.PRICE')
            });
          }
        });

    } else {

      this.focusFirstInvalidField();

      if (!this.hasInlinePriceValidationError()) {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('TRANSLATE.ERROR'),
          detail: this.translateService.instant('TRANSLATE.ERROR.PLEASE.REQUIRED.FIELDS')
        });
      }
    }
  }

  hasControlRequiredError(controlName: string): boolean {
    return !!this.submitted && !!this.userform?.get(controlName)?.hasError('required');
  }

  hasControlMinLengthError(controlName: string): boolean {
    return !!this.submitted && !!this.userform?.get(controlName)?.hasError('minlength');
  }

  hasControlMinValueError(controlName: string): boolean {
    return !!this.submitted && !!this.userform?.get(controlName)?.hasError('min');
  }

  isPriceValueEmpty(): boolean {
    return this.hasControlRequiredError('inputPriceValue');
  }

  isPriceValueZeroOrLess(): boolean {
    if (!this.submitted || this.isPriceValueEmpty()) {
      return false;
    }

    const parsedValue = this.parseInputPriceValue();
    return parsedValue !== undefined && parsedValue <= 0;
  }

  private hasPriceDescriptionMinLengthError(): boolean {
    return this.hasControlMinLengthError('inputPriceDescription');
  }

  private hasInlinePriceValidationError(): boolean {
    return this.isPriceValueEmpty()
      || this.isPriceValueZeroOrLess()
      || this.hasPriceDescriptionMinLengthError();
  }

  private focusFirstInvalidField(): void {
    if (!this.userform) {
      return;
    }

    for (const controlName of this.focusOrder) {
      const control = this.userform.get(controlName);
      if (!control || control.disabled) {
        continue;
      }

      if (control.invalid || control.pending) {
        this.focusField(controlName);
        return;
      }
    }
  }

  private focusField(controlName: string): void {
    const selectorByControl: Record<string, string> = {
      optionNamePrice: '#namePriceNew',
      optionCurrency: '#currencyNew',
      inputPriceValue: '#priceValueNew',
      inputPriceDescription: '#priceDescriptionNew'
    };

    const selector = selectorByControl[controlName];
    if (!selector) {
      return;
    }

    setTimeout(() => {
      const host = document.querySelector(selector) as HTMLElement | null;
      if (!host) {
        return;
      }

      const focusableChild = host.querySelector('input, textarea, .p-dropdown-label, .p-multiselect-label, [tabindex]') as HTMLElement | null;
      const target = focusableChild ?? host;

      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.focus();
    }, 0);
  }
}
